import { getConfig } from './config/environments';

const SCRIPT_ID = 'recaptcha-enterprise-script';
const BADGE_STYLE_ID = 'recaptcha-badge-hidden-style';
let loadPromise: Promise<typeof window.grecaptcha> | null = null;
// The instance our cached promise resolved with. Each widget bundle carries its
// own copy of this module, so this is how a copy notices that another copy has
// re-initialized reCAPTCHA underneath it.
let loadedInstance: typeof window.grecaptcha | null = null;

/**
 * Error surfaced to users when we can't obtain a reCAPTCHA token.
 * Shared so every form fails the same way instead of posting an empty token.
 */
export const RECAPTCHA_ERROR_MESSAGE =
  "We couldn't verify your submission. Please try again.";

/**
 * Hide the floating reCAPTCHA badge.
 *
 * Google permits hiding the badge as long as the reCAPTCHA branding text is
 * shown elsewhere in the user flow (the shared Recaptcha component renders it).
 * See https://developers.google.com/recaptcha/docs/faq
 *
 * Injected via a <style> tag so it also applies inside widget contexts embedded
 * on third-party sites, where we don't control the host stylesheet.
 */
function ensureBadgeHidden(): void {
  if (document.getElementById(BADGE_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = BADGE_STYLE_ID;
  style.textContent = '.grecaptcha-badge { visibility: hidden; }';
  document.head.appendChild(style);
}

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
    // reCAPTCHA's internal config. We never read it, but it must be cleared on
    // teardown or a re-injected script reuses config pointing at removed nodes.
    ___grecaptcha_cfg?: Record<string, unknown>;
  }
}

/**
 * A reCAPTCHA instance whose DOM was destroyed out from under it.
 *
 * Grove/Brightspot navigates as an SPA: it replaces the document body, which
 * removes the badge and the iframes `execute()` depends on. The JS context
 * survives, so `window.grecaptcha` and `enterprise.ready()` still look healthy
 * while `execute()` hangs forever. Globals present but badge gone is the only
 * reliable signal of that state.
 */
function isZombieRecaptcha(): boolean {
  return Boolean(window.grecaptcha?.enterprise) && !document.querySelector('.grecaptcha-badge');
}

/**
 * Whether our cached promise still points at a usable instance. Kept separate
 * from the teardown below: dropping our own cache is always safe, whereas
 * ripping out the shared script must only happen for a genuine zombie.
 */
function isCacheStale(): boolean {
  if (!loadedInstance) {
    return false;
  }

  // Another bundle's copy tore down the zombie and re-initialized; ours is now stale.
  if (loadedInstance !== window.grecaptcha) {
    return true;
  }

  return isZombieRecaptcha();
}

function teardownRecaptcha(): void {
  document.getElementById(SCRIPT_ID)?.remove();
  document.querySelectorAll('.grecaptcha-badge').forEach((badge) => {
    badge.remove();
  });

  delete window.grecaptcha;
  // Stale config still references the removed nodes; a re-injected script would reuse it.
  delete window.___grecaptcha_cfg;
}

export function getRecaptchaSiteKey(): string {
  const config = getConfig();
  return config.recaptchaSiteKey;
}

function appendRecaptchaScript(): Promise<typeof window.grecaptcha> {
  return new Promise((resolve, reject) => {
    ensureBadgeHidden();

    // Runs before we look for an existing script, and synchronously, so a second
    // bundle can never tear down a fresh script another bundle is still loading:
    // once the globals are cleared, this is no longer a zombie.
    if (isZombieRecaptcha()) {
      teardownRecaptcha();
    }

    const existingScript = document.getElementById(SCRIPT_ID);

    if (existingScript) {
      if (window.grecaptcha?.enterprise) {
        resolve(window.grecaptcha);
        return;
      }

      existingScript.addEventListener('load', () => resolve(window.grecaptcha));
      existingScript.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA Enterprise script')));
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${getRecaptchaSiteKey()}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.grecaptcha);
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA Enterprise script'));

    document.head.appendChild(script);
  });
}

export function loadEnterpriseRecaptcha(): Promise<typeof window.grecaptcha> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Window is not defined'));
  }

  if (isCacheStale()) {
    loadPromise = null;
    loadedInstance = null;
  }

  loadPromise ??= appendRecaptchaScript()
    .then((grecaptcha) => {
      if (!grecaptcha) {
        throw new Error('reCAPTCHA Enterprise failed to initialize');
      }

      const enterprise = grecaptcha.enterprise;
      if (!enterprise) {
        throw new Error('reCAPTCHA Enterprise failed to initialize');
      }

      return new Promise<typeof window.grecaptcha>((resolve) => {
        enterprise.ready(() => {
          loadedInstance = grecaptcha;
          resolve(grecaptcha);
        });
      });
    })
    .catch((error) => {
      loadPromise = null;
      throw error;
    });

  return loadPromise;
}

const DEFAULT_EXECUTE_TIMEOUT_MS = 15000;

export async function executeEnterpriseRecaptcha(
  action = 'submit',
  timeoutMs = DEFAULT_EXECUTE_TIMEOUT_MS,
): Promise<string> {
  const grecaptcha = await loadEnterpriseRecaptcha();

  if (!grecaptcha?.enterprise) {
    throw new Error('reCAPTCHA Enterprise is not available');
  }

  return Promise.race([
    grecaptcha.enterprise.execute(getRecaptchaSiteKey(), { action }),
    new Promise<string>((_, reject) =>
      setTimeout(
        () => reject(new Error('reCAPTCHA execute timed out')),
        timeoutMs,
      ),
    ),
  ]);
}

